import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  Check,
  CheckCircle,
  ChevronLeft,
  FileText,
  Loader,
  Mail
} from 'lucide-react';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { useAuth } from '../../../context/AuthContext';
import { AppContext } from '../../../context/AppContext';
import { db } from '../../../firebase/config';
import { useCurrency } from '../../../utils/currencyUtils';
import { buildPayslipRecord } from '../../../utils/payrollUtils';

const PayslipGenerator = () => {
  const { payRunId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  const { formatAmount } = useCurrency();

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [payRun, setPayRun] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [generatedPayslips, setGeneratedPayslips] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      if (!activeBusiness) {
        setError('Select a business before generating payslips.');
        setLoading(false);
        return;
      }

      if (!payRunId) {
        setError('Pay run not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const payRunSnapshot = await getDoc(doc(db, 'payRuns', payRunId));
        if (!payRunSnapshot.exists()) {
          setError('Pay run not found');
          return;
        }

        const payRunData = payRunSnapshot.data();
        const normalizedPayRun = {
          id: payRunSnapshot.id,
          ...payRunData,
          startDate: payRunData.startDate?.toDate() || null,
          endDate: payRunData.endDate?.toDate() || null,
          paymentDate: payRunData.paymentDate?.toDate() || null
        };
        setPayRun(normalizedPayRun);

        const payrollSnapshot = await getDocs(
          query(collection(db, 'employeePayrollDetails'), where('businessId', '==', activeBusiness.id))
        );
        const payrollByEmployeeId = new Map(
          payrollSnapshot.docs.map((snapshot) => [snapshot.data().employeeId, { payrollId: snapshot.id, ...snapshot.data() }])
        );

        const payslipSnapshot = await getDocs(query(collection(db, 'payslips'), where('payRunId', '==', payRunId)));
        const existingPayslips = payslipSnapshot.docs.map((snapshot) => {
          const data = snapshot.data();
          return {
            id: snapshot.id,
            ...data,
            paymentDate: data.paymentDate?.toDate ? data.paymentDate.toDate() : data.paymentDate
          };
        });
        setGeneratedPayslips(existingPayslips);

        const employeeRecords = await Promise.all(
          (payRunData.employeeIds || []).map(async (employeeId) => {
            const payrollRecord = payrollByEmployeeId.get(employeeId) || { employeeId };
            let employee = {
              id: employeeId,
              firstName: '',
              lastName: '',
              email: '',
              position: 'Worker'
            };

            const employeeSnapshot = await getDoc(doc(db, 'teamMembers', employeeId));
            if (employeeSnapshot.exists()) {
              const employeeData = employeeSnapshot.data();
              employee = {
                id: employeeSnapshot.id,
                firstName: employeeData.firstName || '',
                lastName: employeeData.lastName || '',
                email: employeeData.email || '',
                position: employeeData.position || 'Worker'
              };
            }

            const hasPayslip = existingPayslips.some((payslip) => payslip.employeeId === employeeId);

            return {
              ...payrollRecord,
              employee,
              hasPayslip
            };
          })
        );

        setEmployees(employeeRecords);
        setSelectedEmployees(employeeRecords.filter((employee) => !employee.hasPayslip).map((employee) => employee.employeeId));
      } catch (fetchError) {
        console.error('Error loading payslip generator:', fetchError);
        setError('Failed to load pay run data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeBusiness, currentUser, payRunId]);

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees((current) =>
      current.includes(employeeId) ? current.filter((id) => id !== employeeId) : [...current, employeeId]
    );
  };

  const toggleSelectAll = () => {
    const available = employees.filter((employee) => !employee.hasPayslip).map((employee) => employee.employeeId);
    setSelectedEmployees((current) => (current.length === available.length ? [] : available));
  };

  const handleGeneratePayslips = async () => {
    if (selectedEmployees.length === 0 || !payRun) return;

    try {
      setProcessing(true);

      const existingPayslipsSnapshot = await getDocs(
        query(collection(db, 'payslips'), where('businessId', '==', activeBusiness.id), where('employeeId', 'in', selectedEmployees.slice(0, 10)))
      ).catch(() => null);

      const ytdByEmployee = new Map();
      if (existingPayslipsSnapshot) {
        existingPayslipsSnapshot.docs.forEach((snapshot) => {
          const data = snapshot.data();
          ytdByEmployee.set(data.employeeId, {
            ytdEarnings: data.ytdEarnings || 0,
            ytdDeductions: data.ytdDeductions || 0,
            ytdTaxes: data.ytdTaxes || 0
          });
        });
      }

      const newPayslips = [];

      for (const employeeId of selectedEmployees) {
        const employeeRecord = employees.find((employee) => employee.employeeId === employeeId);
        if (!employeeRecord) continue;

        const payslipRecord = buildPayslipRecord({
          payrollRecord: employeeRecord,
          payRun,
          employee: employeeRecord.employee,
          businessId: activeBusiness.id,
          existingYtd: ytdByEmployee.get(employeeId) || {}
        });

        const payslipRef = await addDoc(collection(db, 'payslips'), {
          ...payslipRecord,
          status: 'draft',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        newPayslips.push({
          id: payslipRef.id,
          ...payslipRecord,
          status: 'draft'
        });
      }

      if (newPayslips.length > 0) {
        await updateDoc(doc(db, 'payRuns', payRunId), {
          status: 'processing',
          updatedAt: serverTimestamp()
        });
      }

      setGeneratedPayslips((current) => [...current, ...newPayslips]);
      setEmployees((current) =>
        current.map((employee) =>
          selectedEmployees.includes(employee.employeeId) ? { ...employee, hasPayslip: true } : employee
        )
      );
      setSelectedEmployees([]);
      setPayRun((current) => ({ ...current, status: 'processing' }));
    } catch (generationError) {
      console.error('Error generating payslips:', generationError);
      setError('Failed to generate payslips. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 flex justify-center items-center min-h-[300px]">
        <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
      </div>
    );
  }

  if (error || !payRun) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <button
          onClick={() => navigate('/payroll/runs')}
          className="text-[#190192] hover:text-[#2C0DB5] flex items-center mb-4"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back to Pay Runs
        </button>
        <div className="bg-red-50 rounded-lg p-6 text-center">
          <AlertCircle size={40} className="mx-auto text-red-500 mb-3" />
          <p className="text-red-800">{error || 'Pay run not found'}</p>
        </div>
      </div>
    );
  }

  const pendingEmployees = employees.filter((employee) => !employee.hasPayslip);

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <button
          onClick={() => navigate(`/payroll/runs/${payRunId}`)}
          className="text-[#190192] hover:text-[#2C0DB5] flex items-center"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back to Pay Run
        </button>
        <h1 className="mt-2 text-xl font-bold text-gray-900">Payslips for {payRun.name}</h1>
        <p className="text-sm text-gray-500">
          Generate worker payslips for the period ending {payRun.endDate ? new Date(payRun.endDate).toLocaleDateString() : 'this run'}.
        </p>
      </div>

      <div className="p-6 space-y-6">
        {generatedPayslips.length > 0 && (
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Generated Payslips</h2>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Worker</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Gross</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Net</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {generatedPayslips.map((payslip) => (
                    <tr key={payslip.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payslip.employeeName || payslip.employeeId}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatAmount(payslip.grossAmount)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatAmount(payslip.netAmount)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          payslip.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {payslip.status || 'draft'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link to={`/payroll/payslips/${payslip.id}`} className="text-[#190192] hover:text-[#2C0DB5]">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {pendingEmployees.length > 0 ? (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">Generate Missing Payslips</h2>
              <div className="flex items-center gap-4">
                <button type="button" onClick={toggleSelectAll} className="text-sm text-[#190192] hover:text-[#2C0DB5]">
                  {selectedEmployees.length === pendingEmployees.length ? 'Deselect All' : 'Select All'}
                </button>
                <button
                  type="button"
                  onClick={handleGeneratePayslips}
                  disabled={processing || selectedEmployees.length === 0}
                  className={`px-4 py-2 rounded-lg text-white flex items-center ${
                    processing || selectedEmployees.length === 0
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-[#190192] hover:bg-[#2C0DB5]'
                  }`}
                >
                  {processing ? (
                    <>
                      <Loader size={16} className="mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Check size={16} className="mr-2" />
                      Generate Selected
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Worker</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Base Pay</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Delivery</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingEmployees.map((employee) => (
                    <tr key={employee.employeeId} className={selectedEmployees.includes(employee.employeeId) ? 'bg-purple-50' : 'hover:bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedEmployees.includes(employee.employeeId)}
                          onChange={() => toggleEmployeeSelection(employee.employeeId)}
                          className="h-4 w-4 text-[#190192] border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {employee.employee.firstName} {employee.employee.lastName}
                          </p>
                          <p className="text-sm text-gray-500">{employee.employee.position || 'Worker'}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 capitalize">
                        {employee.employmentType || 'fullTime'} - {employee.salaryType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {formatAmount(employee.baseSalary || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                        {employee.employee.email ? (
                          <span className="inline-flex items-center">
                            <Mail size={14} className="mr-1" />
                            Ready
                          </span>
                        ) : (
                          'No email'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-green-50 rounded-lg p-6 text-center border border-green-200">
            <CheckCircle size={40} className="mx-auto text-green-500 mb-3" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">All Payslips Generated</h3>
            <p className="text-gray-600 mb-4">Every worker in this pay run already has a payslip.</p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => navigate(`/payroll/runs/${payRunId}`)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back to Pay Run
              </button>
              <Link
                to="/payroll/payslips"
                className="px-4 py-2 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5] inline-flex items-center"
              >
                <FileText size={16} className="mr-2" />
                View Payslips
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayslipGenerator;
